import {
  OktonEmptyState,
  OktonSearchFilter,
  OktonSelect,
} from '@okton/platform-ui';
import React, { useState, useMemo } from 'react';
import {
  Box,
  Heading,
  Text,
  Flex,
  Button,
  useColorModeValue,
} from '@chakra-ui/react';
import { RefreshCw } from 'lucide-react';
import { OktonReactTable as Table } from '@okton/platform-ui/react-table';

import Page from '../../layout/IntegraAppPage';

// tipagem para os logs
type Log = {
  id: number;
  dataHora: string;
  tipo: 'Sincronização' | 'Importação' | 'Exportação' | 'Sistema' | 'Erro';
  acao: string;
  status: 'SUCESSO' | 'erro' | 'aviso';
  mensagem: string;
};

// componente Logs
const Logs = () => {
  //  filtros
  const [busca, setBusca] = useState('');
  const [tipo, setTipo] = useState<'todos' | string>('todos');
  const [statusFiltro, setStatusFiltro] = useState<'todos' | string>('todos');
  const [estaCarregando, setEstaCarregando] = useState(false);

  //  claro/escuro
  const corDeFundo = useColorModeValue('white', 'gray.800');
  const corDaBorda = useColorModeValue('gray.200', 'gray.700');
  const corDoTextoSecundario = useColorModeValue('gray.500', 'gray.400');

  // header
  const HeaderCentralizado = ({ titulo }: { titulo: string }) => (
    <Text textAlign="center" fontWeight="600">
      {titulo}
    </Text>
  );

  // Dados dos logs
  const logs = useMemo<Log[]>(
    () => [
      {
        id: 1,
        dataHora: '26/03/2026 10:00:00',
        tipo: 'Sincronização',
        acao: 'Sincronizar Produtos',
        status: 'SUCESSO',
        mensagem: 'Sincronização de produtos concluída com sucesso',
      },
      {
        id: 2,
        dataHora: '26/03/2026 09:45:00',
        tipo: 'Importação',
        acao: 'Importar Dados',
        status: 'erro',
        mensagem: 'Falha na importação: arquivo corrompido',
      },
      {
        id: 3,
        dataHora: '26/03/2026 09:30:00',
        tipo: 'Sistema',
        acao: 'Backup Automático',
        status: 'aviso',
        mensagem:
          'Backup concluído com avisos: alguns arquivos não foram copiados',
      },
      {
        id: 4,
        dataHora: '26/03/2026 09:15:00',
        tipo: 'Exportação',
        acao: 'Exportar Relatório',
        status: 'SUCESSO',
        mensagem: 'Relatório exportado com sucesso',
      },
    ],
    [],
  );

  const logsFiltrados = useMemo(() => {
    return logs.filter(log => {
      const termoBusca = busca.toLowerCase();
      const bateBusca =
        log.mensagem.toLowerCase().includes(termoBusca) ||
        log.acao.toLowerCase().includes(termoBusca) ||
        log.tipo.toLowerCase().includes(termoBusca);

      const bateTipo = tipo === 'todos' || log.tipo === tipo;
      const bateStatus =
        statusFiltro === 'todos' || log.status === statusFiltro;

      return bateBusca && bateTipo && bateStatus;
    });
  }, [busca, tipo, statusFiltro, logs]);

  // tabela
  const colunas = useMemo(
    () => [
      {
        Header: () => <HeaderCentralizado titulo="DATA/HORA" />,
        accessor: 'dataHora',
        Cell: ({ value }: { value: string }) => (
          <Text fontSize="sm" textAlign="center">
            {String(value)}
          </Text>
        ),
      },
      {
        Header: () => <HeaderCentralizado titulo="TIPO" />,
        accessor: 'tipo',
        Cell: ({ value }: { value: string }) => (
          <Text fontWeight="500" textAlign="center">
            {String(value)}
          </Text>
        ),
      },
      {
        Header: () => <HeaderCentralizado titulo="AÇÃO" />,
        accessor: 'acao',
        Cell: ({ value }: { value: string }) => (
          <Text textAlign="center">{String(value)}</Text>
        ),
      },
      {
        Header: () => <HeaderCentralizado titulo="STATUS" />,
        accessor: 'status',
        Cell: ({ value }: { value: string }) => (
          <Text
            color={
              value === 'SUCESSO'
                ? 'green.500'
                : value === 'erro'
                ? 'red.500'
                : 'orange.500'
            }
            fontWeight="500"
            textAlign="center"
          >
            {String(value)}
          </Text>
        ),
      },
      {
        Header: () => <HeaderCentralizado titulo="MENSAGEM" />,
        accessor: 'mensagem',
        Cell: ({ value }: { value: string }) => (
          <Text fontSize="sm" textAlign="center">
            {String(value)}
          </Text>
        ),
      },
    ],
    [],
  );

  return (
    <Page>
      <Box mb={6}>
        <Heading size="lg" mb={1} textAlign="left">
          Logs do Sistema
        </Heading>
        <Text fontSize="sm" color={corDoTextoSecundario}>
          Histórico de ações e eventos do sistema
        </Text>
      </Box>
      <Box
        bg={corDeFundo}
        border="1px solid"
        borderColor={corDaBorda}
        borderRadius="2xl"
        overflow="hidden"
      >
        <Box p={6} borderBottom="1px solid" borderColor={corDaBorda}>
          <Flex align="center" gap="2">
            <OktonSearchFilter
              value={busca}
              onChange={setBusca}
              placeholder="Busca por mensagem, ação ou tipo..."
            />

            <OktonSelect
              name="tipo"
              value={tipo}
              onChange={event => setTipo(event.target.value)}
              options={[
                { value: 'todos', label: 'Todos os tipos' },
                { value: 'Sincronização', label: 'Sincronização' },
                { value: 'Importação', label: 'Importação' },
                { value: 'Exportação', label: 'Exportação' },
                { value: 'Sistema', label: 'Sistema' },
                { value: 'Erro', label: 'Erro' },
              ]}
              w="180px"
            />

            <OktonSelect
              name="status"
              value={statusFiltro}
              onChange={event => setStatusFiltro(event.target.value)}
              options={[
                { value: 'todos', label: 'Todos os status' },
                { value: 'SUCESSO', label: 'Sucesso' },
                { value: 'erro', label: 'Erro' },
                { value: 'aviso', label: 'Aviso' },
              ]}
              w="180px"
            />
            <Button
              leftIcon={<RefreshCw size={14} />}
              size="sm"
              bg="blue.500"
              color="white"
              isLoading={estaCarregando}
              onClick={() => {
                setEstaCarregando(true);
                setTimeout(() => setEstaCarregando(false), 800);
              }}
            >
              Atualizar
            </Button>
          </Flex>
        </Box>

        <Box p={4}>
          <Table columns={colunas} data={logsFiltrados} />
          {logsFiltrados.length === 0 && (
            <OktonEmptyState
              title="Nenhum log encontrado"
              description="Não há registros para os filtros atuais."
            />
          )}
        </Box>
      </Box>
    </Page>
  );
};

export default Logs;
